// Solid, not outline: at the ~14px these sit at inside the brass pill, outline strokes
// collapse into an unreadable smudge — a filled silhouette still reads as flame vs cup.
import { FireIcon, TrophyIcon } from '@heroicons/react/24/solid';
import { DifficultyGauge } from './DifficultyGauge';

/**
 * A session's compact phone info row, inside the frame's masthead: where the learner is in the set,
 * the hand id for editors, the streak/score pill, and the difficulty gauge once the answer is in.
 *
 * Shared because both surfaces built on this frame measure progress the same way — a position in a
 * set, a signed run, a running total — and the row is the only place a phone shows them (the full
 * StatusLine is `display:none` below md). It stays purely presentational: it names nothing about
 * pools, modules or modes, so a session whose progress is shaped differently (a fixed-length round)
 * can still hand it the two numbers, or write its own row for the same `subtitle` slot.
 *
 * Belongs INSIDE the masthead `h1`, `aria-hidden`, beside a screen-reader-only title: that is what
 * keeps the frame's heading non-empty at phone widths, where the visible title is suppressed.
 */
export function MastheadInfoRow({
  position,
  poolSize,
  handId,
  streak,
  score,
  difficulty,
  answered,
  screens = 'md:hidden narrow:flex',
}: {
  position: number;
  poolSize: number;
  /**
   * The hand's identifier, or null for learners (who are not shown it). Strings as well as numbers,
   * matching StatusLine's `idValue`: a tournament hand's id is numeric, a curated problem's serial
   * is a DB text column. Either way it is displayed, never compared.
   */
  handId: number | string | null;
  /**
   * The current run. Optional, and omitted from the pill entirely when absent (feature 028) —
   * mirroring `StatusLine`'s `streak`, whose doc comment states the rule this follows: a session
   * that keeps no run must not render "0", because that states something untrue rather than
   * something unknown. Counting practice keeps no streak; both bidding surfaces do and pass it.
   */
  streak?: number;
  score: number;
  difficulty: number | null;
  /** True once the verdict is in — the difficulty is a post-answer reveal. */
  answered: boolean;
  /**
   * Where the row shows. Defaults to the phone gate Counting uses; the bidding surfaces pass
   * MASTHEAD_PHONE_FLEX (mastheadChrome.ts), which also hides it on the unfolded fold phone at 749
   * wide, where their full masthead and status line show instead (spec 031 FR-026).
   */
  screens?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`flex items-center gap-1.5 font-sans text-sm font-semibold text-cream ${screens}`}
      data-testid="masthead-compact-info"
    >
      <span>
        {position}/{poolSize}
      </span>
      {/* The hand id, beside the position — on phones too (client request, 2026-07-27:
          the client quotes it constantly when reporting a hand, so a trip through the ⓘ
          popup for it is a tap too many). It was phone-hidden for a fortnight because it
          is what tipped the masthead onto a second row; the row now pays for it out of
          the padding and the smaller board instead. The `#` stays — without it the id
          reads as a second number belonging to the position beside it. */}
      {handId !== null && (
        <span className="whitespace-nowrap text-xs font-normal text-sand sm:text-sm">
          #{handId}
        </span>
      )}
      {/* Icons, not the words "Streak:"/"Score:" (client request, 2026-07-27): spelled
          out, the pill ran ~140px and pushed the masthead's icon buttons onto a second
          row. The flame stays put whatever the streak's sign — here it is the LABEL that
          says which number this is, not the praise StatusLine withholds from a losing run
          (see its `streak` doc comment), and dropping it would leave a bare number. The
          words themselves remain one tap away in the (i) details popup. */}
      {/* The 320px tier used to shave this to 11px; 12px is the floor at every viewport
          now (constitution VI, amended 2026-07-29), so the width it needs comes out of
          the gap and the padding instead — the two numbers here are the learner's score. */}
      <span className="flex items-center gap-2 whitespace-nowrap rounded-full bg-brass px-2.5 py-0.5 text-xs text-on-brass xnarrow:gap-1 xnarrow:px-1.5">
        {typeof streak === 'number' && (
          <span className="flex items-center gap-0.5">
            <FireIcon className="h-4 w-4" aria-hidden="true" />
            {streak}
          </span>
        )}
        <span className="flex items-center gap-0.5">
          <TrophyIcon className="h-4 w-4" aria-hidden="true" />
          {score}
        </span>
      </span>
      {/* Post-answer difficulty gauge, as on the old /problems compact row. Scoring
          type and the rest of the facts live behind the ⓘ popup. */}
      {answered && typeof difficulty === 'number' && <DifficultyGauge level={difficulty} />}
    </span>
  );
}
